import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Clock, MapPin } from "lucide-react";
import { MaskedLines } from "./Motion";
import {
  ASSETS,
  PHONE_TEL,
  PHONE_DISPLAY,
  WHATSAPP_URL,
} from "../data/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden grain"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={ASSETS.truckFront}
          alt="Caminhão-guincho FS Guinchos em Porto Alegre"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050505] via-[#050505]/45 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#050505]/75 via-[#050505]/10 to-transparent" />

      {/* 24H side badge */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.1, duration: 0.7 }}
        className="absolute right-4 md:right-10 top-28 md:top-1/3 z-10 hidden sm:flex flex-col items-center"
      >
        <div className="bg-[#e60000] text-white px-4 py-6 red-glow flex flex-col items-center gap-2">
          <Clock size={22} />
          <span className="font-display font-bold text-2xl leading-none">24H</span>
          <span className="font-mono-tech text-[10px] tracking-widest">ATENDIMENTO</span>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-[1400px] w-full mx-auto px-4 md:px-10 pb-16 md:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="inline-flex items-center gap-2 border border-[#e60000]/50 bg-[#e60000]/10 px-4 py-1.5 mb-6"
        >
          <span className="w-2 h-2 rounded-full bg-[#e60000] node-pulse" />
          <span className="font-mono-tech text-xs tracking-[0.2em] text-white uppercase">
            Atendimento 24 Horas
          </span>
        </motion.div>

        <h1
          aria-label="Guincho em Porto Alegre e Região Metropolitana 24 Horas"
          className="font-display font-bold uppercase text-white tracking-tight [word-spacing:0.08em] text-[2.6rem] leading-[0.95] sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl"
        >
          <MaskedLines
            delay={0.35}
            lines={["Guincho em Porto Alegre", "e Região Metropolitana"]}
          />
          <MaskedLines
            delay={0.6}
            lineClass="text-[#e60000]"
            lines={["24 Horas"]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed"
        >
          Atendimento rápido, seguro e confiável para veículos em Porto Alegre,
          Região Metropolitana e outras cidades e estados.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <a
            data-testid="hero-call-btn"
            href={PHONE_TEL}
            className="group flex items-center justify-center gap-3 bg-[#e60000] hover:bg-[#b30000] text-white px-8 py-5 font-bold uppercase tracking-wide text-base transition-colors red-glow"
          >
            <Phone size={22} className="group-hover:animate-pulse" />
            <span className="flex flex-col items-start leading-none">
              <span className="text-xs font-medium opacity-80">Ligar agora</span>
              <span className="text-lg">{PHONE_DISPLAY}</span>
            </span>
          </a>
          <a
            data-testid="hero-whatsapp-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1eb85a] text-black px-8 py-5 font-bold uppercase tracking-wide text-base transition-colors wa-glow"
          >
            <MessageCircle size={22} />
            <span className="flex flex-col items-start leading-none">
              <span className="text-xs font-medium opacity-80">Chamar no WhatsApp</span>
              <span className="text-lg">{PHONE_DISPLAY}</span>
            </span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.7 }}
          className="mt-8 flex items-center gap-2 text-neutral-400 font-mono-tech text-xs sm:text-sm tracking-wide"
        >
          <MapPin size={16} className="text-[#e60000]" />
          Porto Alegre • Região Metropolitana • Outras cidades e estados
        </motion.div>
      </motion.div>
    </section>
  );
};
